/**
 * format-component-list — Auto-generates LLM prompt component list from Zod schemas
 *
 * This module reads the ddCatalog component definitions and their Zod prop schemas
 * to produce a formatted component list string for LLM prompts. By deriving the
 * list directly from the schemas, it eliminates prompt/schema drift.
 *
 * Usage:
 *   import { formatComponentListFromCatalog } from './format-component-list';
 *   const componentList = formatComponentListFromCatalog(ddCatalog.data.components);
 *
 * The output matches the format expected by buildUISpecPrompt():
 *   - ComponentName: { prop: type, ... } - description
 *
 * Task: #1158.P0.2 — Fix prompt/schema drift
 * Agent: Diego <diego@liftping.net>
 */

import type { z } from "zod";

// Catalog component entry shape — matches defineCatalog() output
interface CatalogComponentDef {
  props: z.ZodTypeAny;
  description: string;
  slots?: string[];
}

// Zod v4 internals are not typed. This helper provides typed access to _def
// properties used for schema introspection. This is intentional — there is no
// public API for serializing Zod schemas to human-readable prop signatures.
// Pin @json-render/* versions to avoid breakage on Zod major upgrades.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function def(schema: z.ZodTypeAny): any {
  return (schema as any)._def;
}

/**
 * Serialize a Zod schema shape into a human-readable prop signature.
 *
 * Uses _def.type (not _def.typeName) for type discrimination, matching
 * the Zod schema structure used by @json-render/core's defineCatalog().
 *
 * Examples:
 *   { title: string, subtitle?: string, variant: "standard" | "elevated" | "premium" }
 */
function serializeZodShape(schema: z.ZodTypeAny): string {
  const shape = getZodShape(schema);
  if (!shape) return "{}";

  const parts: string[] = [];

  for (const [key, fieldSchema] of Object.entries(shape)) {
    const field = fieldSchema as z.ZodTypeAny;
    const { typeName, isOptional } = resolveFieldType(field);
    const optMark = isOptional ? "?" : "";
    parts.push(`${key}${optMark}: ${typeName}`);
  }

  return `{ ${parts.join(", ")} }`;
}

/**
 * Extract the shape from a Zod object schema.
 * The catalog's Zod schemas expose .shape as a property (not a function).
 */
function getZodShape(
  schema: z.ZodTypeAny
): Record<string, z.ZodTypeAny> | null {
  const defType = def(schema)?.type as string | undefined;

  // Unwrap ZodEffects
  if (defType === "effects") {
    return getZodShape(def(schema).schema);
  }

  // ZodObject — .shape is a direct property in this Zod build
  if (defType === "object" || schema.constructor?.name === "ZodObject") {
    // .shape can be a property or a function depending on Zod version
    const shape =
      typeof def(schema)?.shape === "function"
        ? def(schema).shape()
        : (schema as any).shape;
    return shape as Record<string, z.ZodTypeAny> | null;
  }

  return null;
}

/**
 * Resolve a field schema to its display type name and optionality.
 */
function resolveFieldType(field: z.ZodTypeAny): {
  typeName: string;
  isOptional: boolean;
} {
  let current = field;
  let isOptional = false;

  // Unwrap optional
  while (getDefType(current) === "optional") {
    isOptional = true;
    current = def(current).innerType;
  }

  // Unwrap default (has an inner type + default value)
  if (getDefType(current) === "default") {
    current = def(current).innerType;
  }

  const resolvedTypeName = getTypeName(current);
  return { typeName: resolvedTypeName, isOptional };
}

/** Helper to get _def.type safely */
function getDefType(schema: z.ZodTypeAny): string | undefined {
  return def(schema)?.type as string | undefined;
}

/**
 * Get a human-readable type name from a Zod schema.
 */
function getTypeName(schema: z.ZodTypeAny): string {
  const defType = getDefType(schema);

  if (defType === "string") return "string";
  if (defType === "number") return "number";
  if (defType === "boolean") return "boolean";

  if (defType === "enum") {
    // .options is the standard accessor for enum values
    const values = (schema as any).options as string[] | undefined;
    if (values) {
      return values.map((v: string) => `"${v}"`).join(" | ");
    }
    return "string";
  }

  if (defType === "array") {
    const element = def(schema)?.element;
    const innerType = element ? getTypeName(element) : "unknown";
    return `Array<${innerType}>`;
  }

  if (defType === "union") {
    const options = def(schema)?.options as z.ZodTypeAny[] | undefined;
    if (options) {
      return options.map(getTypeName).join(" | ");
    }
    return "unknown";
  }

  if (defType === "literal") {
    const val = def(schema)?.value;
    return typeof val === "string" ? `"${val}"` : String(val);
  }

  if (defType === "nullable") {
    return `${getTypeName(def(schema).innerType)} | null`;
  }

  if (defType === "default") {
    return getTypeName(def(schema).innerType);
  }

  if (defType === "optional") {
    return getTypeName(def(schema).innerType);
  }

  return "unknown";
}

/**
 * Format the full component list from the catalog component definitions.
 * This is the single function that generates the "Available Components"
 * section of the LLM prompt.
 *
 * By reading prop types directly from Zod schemas, this eliminates the
 * possibility of prompt/schema drift for prop names, types, and enum values.
 *
 * @param catalogComponents - Map of component name to { description, props (Zod schema) }
 * @returns Formatted multi-line string for use in the LLM prompt
 */
export function formatComponentListFromCatalog(
  catalogComponents: Record<string, CatalogComponentDef>
): string {
  const lines: string[] = [];

  for (const [name, def] of Object.entries(catalogComponents)) {
    const propsSignature = serializeZodShape(def.props);
    const hasSlots =
      def.slots && def.slots.length > 0 && def.slots[0] !== undefined;
    const slotSuffix = hasSlots ? " [accepts children]" : "";
    lines.push(
      `- ${name}: ${propsSignature} - ${def.description}${slotSuffix}`
    );
  }

  return lines.join("\n");
}

/**
 * Extract component names from the catalog.
 */
export function extractComponentNames(
  catalogComponents: Record<string, CatalogComponentDef>
): string[] {
  return Object.keys(catalogComponents);
}
